import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { RevealOnScroll } from "@/components/site/Reveal";
import { X, Plus } from "lucide-react";

const SPEC_KEYS = ["mileage_km", "engine", "seats", "transmission", "battery", "range_km", "fuel_economy", "price_ghs"];
const LABELS = {
  mileage_km: "Mileage",
  engine: "Engine",
  seats: "Seats",
  transmission: "Transmission",
  battery: "Battery",
  range_km: "EV Range",
  fuel_economy: "Consumption",
  price_ghs: "Price",
};

function fmt(k, v) {
  if (v == null || v === "") return "—";
  if (k === "price_ghs") return new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS", maximumFractionDigits: 0 }).format(v);
  if (k === "mileage_km") return `${v.toLocaleString()} km`;
  if (k === "range_km") return `${v} km`;
  return v;
}

export default function Compare() {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setAll(r.data);
      const add = params.get("add");
      if (add) {
        const v = r.data.find((x) => x.slug === add);
        if (v) setSelected([v]);
      }
    }).catch(() => {});
  }, [params]);

  const remove = (slug) => setSelected((s) => s.filter((v) => v.slug !== slug));
  const add = (v) => setSelected((s) => s.length < 3 && !s.find((x) => x.slug === v.slug) ? [...s, v] : s);

  const empty = 3 - selected.length;

  return (
    <div data-testid="compare-page" className="pt-40 pb-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-6">Side by Side</div>
          <RevealOnScroll
            element="h1"
            className="h-editorial text-[clamp(3rem,7vw,6rem)]"
            lines={["Compare up", "to <em>three</em>."]}
          />
        </div>

        {/* Selection UI */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {selected.map((v) => (
            <div key={v.slug} className="relative border border-[#C5A059]/40 p-4" data-testid={`compare-slot-${v.slug}`}>
              <button onClick={() => remove(v.slug)} className="absolute top-3 right-3 text-white/60 hover:text-white" aria-label="Remove">
                <X size={16} />
              </button>
              <img src={v.images[0]} alt="" className="w-full aspect-video object-cover mb-3" />
              <div className="font-display text-xl">{v.make} {v.model}</div>
              <div className="text-xs text-white/50">{v.year}</div>
            </div>
          ))}
          {Array.from({ length: empty }).map((_, i) => (
            <div key={i} className="border border-dashed border-white/15 p-4 flex flex-col items-center justify-center min-h-[220px] text-white/50">
              <Plus size={22} />
              <div className="mt-3 text-xs tracking-[0.2em] uppercase">Add a vehicle</div>
            </div>
          ))}
        </div>

        {/* Picker */}
        <div className="mb-16">
          <div className="overline mb-4">Add from inventory</div>
          <div className="flex flex-wrap gap-2">
            {all.map((v) => {
              const isSel = selected.find((x) => x.slug === v.slug);
              return (
                <button
                  key={v.slug}
                  disabled={isSel || selected.length >= 3}
                  onClick={() => add(v)}
                  data-testid={`compare-add-${v.slug}`}
                  className={`px-4 py-2 text-xs border transition ${isSel ? "opacity-40 border-white/10" : "border-white/15 hover:border-[#C5A059]/60 hover:text-[#C5A059]"}`}
                >
                  {v.make} {v.model}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        {selected.length > 0 && (
          <div className="border-t border-white/10">
            {SPEC_KEYS.map((k) => (
              <div key={k} className="grid grid-cols-4 border-b border-white/5 py-5 items-center">
                <div className="text-xs tracking-[0.2em] uppercase text-white/45">{LABELS[k]}</div>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="font-display text-lg">
                    {selected[i] ? fmt(k, selected[i][k]) : ""}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
